/**
 * Service Toggles Utility
 * Manages enabled/disabled state for backend APIs.
 * Used for demo purposes to showcase service virtualization —
 * disabling a service simulates it being unavailable.
 *
 * Toggle state is scoped per user so multiple demo users on the
 * same deployed instance don't interfere with each other.
 * User identity is derived automatically from the JWT stored in
 * sessionStorage — no React context wiring needed.
 *
 * This is a plain JS module (not a React hook) so it can be
 * imported from API service files (billingApi.js, etc.).
 */

const LS_KEY_BASE = 'triserveenergy-service-toggles';
const JWT_STORAGE_KEY = 'tse.jwt'; // must match authService.js

const DEFAULT_STATE = {
  auth: true,
  billing: true,
  serviceRequests: true,
  salesforce: true,
  sap: true,
};

// ─── User identity from JWT ──────────────────────

/**
 * Decode the payload of a JWT (middle segment) without a library.
 * Returns the parsed claims object, or null on failure.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Base64url → Base64 → decode
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Derive a short, stable user identifier from the JWT in sessionStorage.
 * Tries claim fields in order: email, unique_name, sub.
 * Returns null if no JWT is present or no usable claim is found.
 */
function getCurrentUserId() {
  try {
    const s = typeof window !== 'undefined' ? window.sessionStorage : null;
    if (!s) return null;
    const token = s.getItem(JWT_STORAGE_KEY);
    if (!token) return null;
    const claims = decodeJwtPayload(token);
    if (!claims) return null;
    // .NET Identity typically uses one of these:
    return (
      claims.email ||
      claims.unique_name ||
      claims.sub ||
      claims.preferred_username ||
      claims.name ||
      null
    );
  } catch {
    return null;
  }
}

/**
 * Build the localStorage key, scoped to the current user when available.
 * Falls back to the un-scoped key for unauthenticated / pre-login state.
 */
function lsKey() {
  const userId = getCurrentUserId();
  return userId ? `${LS_KEY_BASE}--${userId}` : LS_KEY_BASE;
}

// ─── Public API ──────────────────────────────────

/**
 * Read current toggle state from localStorage (scoped to logged-in user).
 * Returns an object like { auth: true, billing: false, serviceRequests: true }.
 */
export function getServiceToggles() {
  try {
    const raw = localStorage.getItem(lsKey());
    if (!raw) return { ...DEFAULT_STATE };
    return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

/**
 * Persist toggle state to localStorage (scoped to logged-in user).
 * @param {Object} toggles - e.g. { auth: true, billing: false }
 */
export function setServiceToggles(toggles) {
  localStorage.setItem(lsKey(), JSON.stringify(toggles));
}

/**
 * Check whether a specific service is enabled.
 * @param {'auth'|'billing'|'serviceRequests'|'salesforce'|'sap'} serviceKey
 * @returns {boolean}
 */
export function isServiceEnabled(serviceKey) {
  const toggles = getServiceToggles();
  return toggles[serviceKey] !== false;
}

/**
 * Throw a realistic-looking error when a disabled service is called.
 * Simulates a network/connection failure that you'd see when a real
 * service is down — perfect for demoing service virtualization.
 * @param {string} serviceName - Human-friendly name, e.g. "Billing API"
 */
export function throwServiceUnavailable(serviceName) {
  const err = new Error(
    `${serviceName} is currently unavailable. The service has been disabled in Settings → API Configuration.`
  );
  err.code = 'SERVICE_DISABLED';
  err.serviceName = serviceName;
  throw err;
}

/**
 * Guard function — call at the top of any API function.
 * If the service is disabled, it throws immediately.
 * @param {'auth'|'billing'|'serviceRequests'|'salesforce'|'sap'} serviceKey
 * @param {string} serviceName
 */
export function guardService(serviceKey, serviceName) {
  if (!isServiceEnabled(serviceKey)) {
    throwServiceUnavailable(serviceName);
  }
}

/**
 * Reset toggles for the current user back to defaults.
 */
export function resetServiceToggles() {
  localStorage.removeItem(lsKey());
}

/**
 * Return the current user identifier (for debugging / display).
 * Returns null when unauthenticated.
 */
export function getTogglesUserId() {
  return getCurrentUserId();
}

export default {
  getServiceToggles,
  setServiceToggles,
  isServiceEnabled,
  guardService,
  resetServiceToggles,
  getTogglesUserId,
};
